// The office's possible duplicates (BL-029): pairs of customers that share a phone, an email or a
// service address, for those who can merge. Opening one goes to its record, where "Merge into
// another…" is; a pair that really is two people (a tenant, a new owner) is just left.

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { dupeReason, type Shared } from '../shared/new-customer-model.ts';

interface Who {
  id: string;
  number: string;
  displayName: string;
}
export interface Pair {
  a: Who;
  b: Who;
  same: Shared[];
}

/** The office's possible duplicates, once customers.duplicatePairs answers; null until then or if it can't. */
export function useDuplicatePairs(): Pair[] | null {
  const [pairs, setPairs] = useState<Pair[] | null>(null);
  useEffect(() => {
    client
      .call<{ pairs: Pair[] }>('customers.duplicatePairs', {})
      .then((r) => setPairs(r.pairs))
      .catch(() => setPairs(null));
  }, []);
  return pairs;
}

/** What they share, in words: "Same phone number and address" (a pair's number is a phone number). */
export const pairSame = (same: readonly Shared[]): string => `Same ${dupeReason(same, true)}`;

/** The muted line before the pairs. */
export const PAIRS_NOTE =
  'Customers that share a phone, an email or an address. Open one to merge it into the other, or leave them if they really are different people.';

export function DuplicatePairs({ onOpen }: { onOpen: (id: string) => void }) {
  const pairs = useDuplicatePairs();
  if (!pairs?.length) return null;
  return (
    <details className="sheet card dup-pairs">
      <summary>Possible duplicates ({pairs.length})</summary>
      <p className="muted small">{PAIRS_NOTE}</p>
      <ul className="dup-list">
        {pairs.map((p) => (
          <li key={`${p.a.id}:${p.b.id}`}>
            <span className="muted small">{pairSame(p.same)}</span>
            <span className="row">
              {[p.a, p.b].map((c) => (
                <button key={c.id} className="btn soft" onClick={() => onOpen(c.id)}>
                  {c.displayName} <span className="muted small">{c.number}</span>
                </button>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}
